import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Mail, Wheat } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const ExitIntentModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check if user has already seen modal in this session
    const modalShown = sessionStorage.getItem('exitIntentShown');
    if (modalShown) {
      setHasShown(true);
      return;
    }

    // Exit intent detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown && !isOpen) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    // Time-based trigger (after 45 seconds)
    const timer = setTimeout(() => {
      if (!hasShown && !isOpen) {
        setIsOpen(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    }, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
    };
  }, [hasShown, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('idle');

    // IMPORTANT: read values before disabling inputs (disabled inputs are omitted from FormData)
    const formData = new FormData(e.currentTarget);
    const firstName = String(formData.get('first_name') || '').trim();
    const email = String(formData.get('email') || '').trim();

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/subscribe`,
        {
          first_name: firstName,
          email: email
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );

      if (response.status === 201 && response.data.success) {
        setMessage(response.data.message || 'Thank you for subscribing!');
        // Tiny delay avoids conditional-render glitches on some devices/browsers
        setTimeout(() => {
          setSubmitStatus('success');
        }, 50);
        setTimeout(() => {
          setIsOpen(false);
        }, 2500);
        e.currentTarget.reset();
      }

    } catch (error) {
      setSubmitStatus('error');
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          setMessage(detail);
        } else if (Array.isArray(detail)) {
          setMessage(detail.map((d) => d?.msg).filter(Boolean).join(' ') || 'Something went wrong. Please try again.');
        } else if (detail && typeof detail === 'object') {
          setMessage(detail.message || 'Something went wrong. Please try again.');
        } else {
          setMessage('Something went wrong. Please try again.');
        }
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-md w-full mx-4 p-8 relative shadow-2xl animate-in zoom-in duration-300">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-[#736c64] hover:text-[#1e1919] transition-colors"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#a8b5a0]/20 rounded-full mb-4">
            <Wheat className="h-8 w-8 text-[#a8b5a0]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1e1919] mb-2">
            Before You Go...
          </h2>
          <p className="text-[#736c64]">
            Join our community and get exclusive sourdough tips, new recipes, and be the first to know about new products!
          </p>
        </div>

        {submitStatus === 'success' ? (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-green-800 font-medium">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                name="first_name"
                placeholder="First Name"
                disabled={isSubmitting}
                className="w-full border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
              />
            </div>
            <div>
              <Input
                type="email"
                name="email"
                placeholder="Email Address"
                disabled={isSubmitting}
                className="w-full border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#a8b5a0] hover:bg-[#8fa087] text-white py-6"
            >
              {isSubmitting ? 'Joining...' : 'Join the Community'}
            </Button>

            {submitStatus === 'error' && (
              <p className="text-red-600 text-sm text-center">{message}</p>
            )}
          </form>
        )}

        <p className="text-xs text-[#736c64] text-center mt-4">
          We respect your privacy. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};

export default ExitIntentModal;
